/**
 * Direction icon rendering module
 * Handles icon creation, caching, and drawing
 */

import { ICON_INNER_RADIUS, ICON_RADIUS } from '../../constants'
import type { DirectionIcon } from './types'

// Icon cache: stores pre-rendered icons as ImageBitmap
const iconCache = new Map<string, ImageBitmap>()

/**
 * Creates a cache key for icon lookup
 */
function createCacheKey(color: string, pixelRatio: number): string {
  return `${color}-${pixelRatio}`
}

/**
 * Gets or creates a direction icon with the specified color and pixel ratio
 * Icons are cached as ImageBitmap for optimal performance
 */
export function getDirectionIcon(color: string, pixelRatio: number): ImageBitmap {
  const cacheKey = createCacheKey(color, pixelRatio)

  if (iconCache.has(cacheKey)) {
    return iconCache.get(cacheKey)!
  }

  const offScreen = new OffscreenCanvas(ICON_RADIUS * 2 * pixelRatio, ICON_RADIUS * 2 * pixelRatio)
  const offScreenCtx = offScreen.getContext('2d')

  if (!offScreenCtx) {
    throw new Error('OffscreenCanvasRenderingContext2D not supported')
  }

  const x = ICON_RADIUS * pixelRatio
  const y = ICON_RADIUS * pixelRatio

  offScreenCtx.lineJoin = 'round'
  offScreenCtx.lineCap = 'round'

  // Draw outer circle
  offScreenCtx.beginPath()
  offScreenCtx.strokeStyle = color
  offScreenCtx.fillStyle = color
  offScreenCtx.arc(x, y, ICON_RADIUS * pixelRatio, 0, 2 * Math.PI)
  offScreenCtx.fill()

  // Draw inner circle
  offScreenCtx.beginPath()
  offScreenCtx.strokeStyle = '#FFFFFF'
  offScreenCtx.fillStyle = '#FFFFFF'
  offScreenCtx.arc(x, y, ICON_INNER_RADIUS * pixelRatio, 0, 2 * Math.PI)
  offScreenCtx.fill()

  // Draw arrow
  offScreenCtx.beginPath()
  offScreenCtx.strokeStyle = color
  offScreenCtx.fillStyle = color
  offScreenCtx.lineWidth = 2 * pixelRatio

  offScreenCtx.translate(x, y)
  offScreenCtx.moveTo(pixelRatio * (8.10294 - ICON_RADIUS), pixelRatio * (6.23448 - ICON_RADIUS))
  offScreenCtx.lineTo(pixelRatio * (11.5496 - ICON_RADIUS), pixelRatio * (9.23904 - ICON_RADIUS))
  offScreenCtx.lineTo(pixelRatio * (8.54504 - ICON_RADIUS), pixelRatio * (12.6857 - ICON_RADIUS))

  // Stroke everything
  offScreenCtx.stroke()

  // Cache the image
  const image = offScreen.transferToImageBitmap()
  iconCache.set(cacheKey, image)
  return image
}

/**
 * Draws a direction icon on the canvas
 */
export function drawDirectionIcon(
  ctx: CanvasRenderingContext2D,
  icon: DirectionIcon,
  pixelRatio: number,
): void {
  const { x, y, color, rotation } = icon
  const image = getDirectionIcon(color, pixelRatio)

  ctx.save()
  ctx.translate(x, y)
  ctx.rotate(rotation)
  ctx.drawImage(image, -ICON_RADIUS * pixelRatio, -ICON_RADIUS * pixelRatio)
  ctx.restore()
}

/**
 * Clears the icon cache
 * Useful for memory management or when icon rendering changes
 */
export function clearIconCache(): void {
  iconCache.clear()
}

/**
 * Gets the current size of the icon cache
 */
export function getIconCacheSize(): number {
  return iconCache.size
}
